/**
 * Extraction mode of the Materials Chemistry tool: choose a method from a metal's position in the
 * reactivity series relative to carbon. Accepted methods and equations are reviewed data; option
 * feedback is generated from the metal's position so every wrong choice gets a specific reason.
 */
import type { MaterialsChoiceCase, MaterialsOption } from './materialsChemistry';

export type ExtractionMethod = 'electrolysis' | 'carbon' | 'heating' | 'native';

export const EXTRACTION_METHOD_LABELS: Record<ExtractionMethod, string> = {
  electrolysis: 'Electrolysis (molten compound or solution)',
  carbon: 'Reduction with carbon (or carbon monoxide)',
  heating: 'Heating the ore in air (no reducing agent)',
  native: 'Found as the element — separate it physically',
};

export const EXTRACTION_METHOD_ORDER: ExtractionMethod[] = ['electrolysis', 'carbon', 'heating', 'native'];

/** Most reactive first. 'C' marks where carbon sits for reduction at high temperature. */
export const REACTIVITY_LADDER = ['K', 'Na', 'Ca', 'Mg', 'Al', 'C', 'Zn', 'Fe', 'Sn', 'Pb', 'Cu', 'Hg', 'Au'] as const;
export const CARBON_MARKER = 'C';
/** Metals below copper (Hg, Au) are unreactive enough to occur natively at least occasionally. */
const LEAST_REACTIVE_COMMON_METAL = 'Cu';

export interface ExtractionMetal {
  symbol: string;
  name: string;
  ore: string;
  accepted: ExtractionMethod[];
  equation?: string;
  note: string;
}

// prettier-ignore
export const EXTRACTION_METALS: ExtractionMetal[] = [
  { symbol: 'Na', name: 'Sodium', ore: 'rock salt, NaCl', accepted: ['electrolysis'], equation: '2NaCl(l) → 2Na(l) + Cl₂(g)', note: 'Molten sodium chloride is electrolysed (Downs cell); Na forms at the cathode and Cl₂ at the anode.' },
  { symbol: 'Mg', name: 'Magnesium', ore: 'magnesium chloride from sea water or brines, MgCl₂', accepted: ['electrolysis'], equation: 'MgCl₂(l) → Mg(l) + Cl₂(g)', note: 'Electrolysis of molten MgCl₂ is the classic route. Much magnesium is now also made by reducing MgO with silicon, which is still not a carbon reduction.' },
  { symbol: 'Al', name: 'Aluminium', ore: 'bauxite, refined to alumina, Al₂O₃', accepted: ['electrolysis'], equation: '2Al₂O₃(l) → 4Al(l) + 3O₂(g)', note: 'Alumina is dissolved in molten cryolite to lower the operating temperature (Hall–Héroult process). The oxygen reacts with the carbon anodes, which must be replaced.' },
  { symbol: 'Zn', name: 'Zinc', ore: 'sphalerite, ZnS, roasted to ZnO', accepted: ['carbon', 'electrolysis'], equation: 'ZnO(s) + C(s) → Zn(g) + CO(g)', note: 'Zinc is just below carbon, so carbon reduces ZnO at high temperature. Most zinc today is made by electrolysis of an aqueous zinc sulfate solution instead (electrowinning), because it gives very pure zinc — both answers are accepted.' },
  { symbol: 'Fe', name: 'Iron', ore: 'haematite, Fe₂O₃', accepted: ['carbon'], equation: 'Fe₂O₃(s) + 3CO(g) → 2Fe(l) + 3CO₂(g)', note: 'In a blast furnace, coke (carbon) burns to carbon monoxide, which reduces the iron ore.' },
  { symbol: 'Sn', name: 'Tin', ore: 'cassiterite, SnO₂', accepted: ['carbon'], equation: 'SnO₂(s) + 2C(s) → Sn(l) + 2CO(g)', note: 'Tin oxide is smelted with carbon; tin is below carbon in the reactivity series.' },
  { symbol: 'Pb', name: 'Lead', ore: 'galena, PbS, roasted to PbO', accepted: ['carbon'], equation: '2PbO(s) + C(s) → 2Pb(l) + CO₂(g)', note: 'The sulfide ore is first roasted in air to the oxide, which carbon then reduces.' },
  { symbol: 'Hg', name: 'Mercury', ore: 'cinnabar, HgS', accepted: ['heating'], equation: 'HgS(s) + O₂(g) → Hg(g) + SO₂(g)', note: 'Mercury is so unreactive that heating its sulfide in air releases the metal as a vapour, which is condensed. Mercury vapour is highly toxic.' },
  { symbol: 'Au', name: 'Gold', ore: 'native gold in quartz veins and river gravels', accepted: ['native'], note: 'Gold is so unreactive that it occurs as the element. Fine gold is often dissolved out of crushed rock and recovered chemically, but the gold in the rock is already the element.' },
];

export function ladderIndex(symbol: string): number {
  return (REACTIVITY_LADDER as readonly string[]).indexOf(symbol);
}

export function isAboveCarbon(symbol: string): boolean {
  const index = ladderIndex(symbol);
  return index >= 0 && index < ladderIndex(CARBON_MARKER);
}

export function extractionMetalBySymbol(symbol: string): ExtractionMetal | undefined {
  return EXTRACTION_METALS.find((metal) => metal.symbol === symbol);
}

function cheaperRoute(metal: ExtractionMetal): string {
  if (metal.accepted.includes('native')) return `${metal.name.toLowerCase()} is found as the element, so it only needs separating`;
  if (metal.accepted.includes('heating')) return 'simply heating the ore in air releases it, which is far cheaper';
  return 'reduction with carbon is far cheaper';
}

/** Specific feedback for any method choice, generated from the metal's reactivity position. */
export function extractionFeedback(metal: ExtractionMetal, method: ExtractionMethod): string {
  if (metal.accepted.includes(method)) {
    const position = isAboveCarbon(metal.symbol)
      ? `${metal.name} is above carbon in the reactivity series, so carbon cannot reduce its compounds.`
      : `${metal.name} is below carbon in the reactivity series.`;
    return `Correct. ${position} ${metal.note}`;
  }
  const above = isAboveCarbon(metal.symbol);
  switch (method) {
    case 'electrolysis':
      return `Electrolysis can extract almost any metal, but it uses a lot of electrical energy. ${metal.name} is below carbon, and ${cheaperRoute(metal)}.`;
    case 'carbon':
      if (above) return `${metal.name} is more reactive than carbon, so carbon cannot remove oxygen or chlorine from its compounds. A stronger method is needed.`;
      return `Carbon is not needed: ${cheaperRoute(metal)}.`;
    case 'heating':
      if (above) return `Compounds of a reactive metal like ${metal.name.toLowerCase()} are very stable; heating alone will not release the metal.`;
      if (metal.accepted.includes('native')) return `${metal.name} is not found as a compound to heat — it occurs as the element.`;
      return `Heating ${metal.name.toLowerCase()} ore in air alone does not release the metal: its oxide is too stable. It must be reduced with carbon.`;
    case 'native':
      if (ladderIndex(metal.symbol) > ladderIndex(LEAST_REACTIVE_COMMON_METAL)) {
        return `Small amounts of native ${metal.name.toLowerCase()} do occur, but almost all of it is mined as a compound: ${metal.ore}.`;
      }
      return `${metal.name} is too reactive to be found as the element in useful amounts. It is mined as compounds such as ${metal.ore}.`;
    default:
      return 'This method is not in the reviewed list.';
  }
}

function extractionOptions(metal: ExtractionMetal): MaterialsOption[] {
  return EXTRACTION_METHOD_ORDER.map((method) => ({
    id: method,
    label: EXTRACTION_METHOD_LABELS[method],
    correct: metal.accepted.includes(method),
    feedback: extractionFeedback(metal, method),
  }));
}

export const EXTRACTION_CASES: MaterialsChoiceCase[] = EXTRACTION_METALS.map((metal): MaterialsChoiceCase => ({
  id: `extract-${metal.symbol.toLowerCase()}`,
  kind: 'choice',
  title: metal.name,
  prompt: `${metal.name} (${metal.symbol}) is obtained from ${metal.ore}. Use its position in the reactivity series to choose the extraction method.`,
  visual: { type: 'ladder', metal: metal.symbol },
  options: extractionOptions(metal),
  takeaway: 'The more reactive the metal, the more stable its compounds and the more energy is needed to extract it.',
  equation: metal.equation,
}));
